import { ImageFile } from "./ImageFile";
import { setLoaded } from "@stores";
import { ModelLoader } from "./ModelLoader";
import { AnimationLoader } from "./AnimationLoader";
import heightMap from "@assets/img/heightmap/heightmap-01.png";
import normalMap from "@assets/img/normalmap/normalmap-01.png";
import skyboxPosX from "@assets/img/skybox/posx.jpg";
import skyboxNegX from "@assets/img/skybox/negx.jpg";
import skyboxPosY from "@assets/img/skybox/posy.jpg";
import skyboxNegY from "@assets/img/skybox/negy.jpg";
import skyboxPosZ from "@assets/img/skybox/posz.jpg";
import skyboxNegZ from "@assets/img/skybox/negz.jpg";
import playerModel from "@assets/models/player/player.fbx";
import playerIdleAnimation from "@assets/models/player/animations/player-idle.fbx";
import playerRunningAnimation from "@assets/models/player/animations/player-running.fbx";
import playerWalkingAnimation from "@assets/models/player/animations/player-walking.fbx";
import skorpionModel from "@assets/models/guns/skorpion/škorpion.fbx";
import { SkyboxLoader } from "./SkyboxLoader";
class AssetLoader {
    assetSources;
    loadedAssets;
    assets;
    static instance;
    /**
     * AssetLoader loads all the assets the game needs.
     * @throws If an asset fails to load.
     * @todo Make `assetsSources` dynamic.
     */
    constructor() {
        this.loadedAssets = [];
        this.assetSources = [
            heightMap,
            normalMap,
            playerModel,
            playerIdleAnimation,
            playerWalkingAnimation,
            playerRunningAnimation,
            skorpionModel,
            "skybox",
        ];
        this.assets = {
            terrain: this.loadTerrainAssets(),
            player: this.loadPlayerAssets(),
            enemy: this.loadEnemyAssets(),
            weapons: this.loadGunAssets(),
        };
    }
    /**
     * Load all assets related to the terrain.
     * @returns An object literal with all terrain-related assets.
     * @throws If an image fails to load.
     */
    loadTerrainAssets() {
        return {
            heightMap: this.loadImage(heightMap),
            normalMap: this.loadImage(normalMap),
            skybox: this.loadCubeTexture([
                skyboxPosX,
                skyboxNegX,
                skyboxPosY,
                skyboxNegY,
                skyboxPosZ,
                skyboxNegZ,
            ]),
        };
    }
    /**
     * Load all assets related to the player object.
     * @returns An object literal with all player-related assets.
     * @throws If the model fails to load.
     */
    loadPlayerAssets() {
        return {
            modelLoader: this.loadModel(playerModel),
            animations: {
                idle: this.loadAnimation(playerIdleAnimation),
                walking: this.loadAnimation(playerWalkingAnimation),
                running: this.loadAnimation(playerRunningAnimation),
            },
        };
    }
    /**
     * Load all assets related to the enemy objects.
     * @returns An object literal with all enemy-related assets.
     * @throws If the model fails to load.
     * @todo Create enemy models and animations.
     */
    loadEnemyAssets() {
        return {
            modelLoader: this.loadModel(playerModel),
            animations: {
                idle: this.loadAnimation(playerIdleAnimation),
                walking: this.loadAnimation(playerWalkingAnimation),
                running: this.loadAnimation(playerRunningAnimation),
            },
        };
    }
    loadGunAssets() {
        return {
            skorpion: {
                modelLoader: this.loadModel(skorpionModel),
            },
        };
    }
    /**
     * Create a new ImageFile instance.
     * @param source the source of the image.
     * @returns the created ImageFile instance.
     * @throws If the image fails to load.
     */
    loadImage(source) {
        return new ImageFile(source, () => this.handleLoadedAsset(source));
    }
    /**
     * Create a new Cube Texture instance.
     * @param source A list of 6 images that map to the cube texture.
     * @returns The created cube texture.
     * @throws If an image fails to load.
     */
    loadCubeTexture(source) {
        return new SkyboxLoader(source, () => this.handleLoadedAsset("skybox"));
    }
    /**
     * Create a new 3D model instance.
     * @param source the source of the 3D model.
     * @returns the created ModelLoader instance.
     * @throws If the model fails to load.
     */
    loadModel(source) {
        return new ModelLoader(source, () => this.handleLoadedAsset(source));
    }
    /**
     * Create a new 3D animation instance.
     * @param source the source of the 3D animation.
     * @returns the created AnimationLoader instance.
     * @throws If the animation fails to load.
     */
    loadAnimation(source) {
        return new AnimationLoader(source, () => this.handleLoadedAsset(source));
    }
    /**
     * Handle an asset being loaded.
     * Add the source of the asset to the `loadedAssets` property, and update the solid-js signal keeping track of asset loading.
     * @param source The source of the asset that was loaded.
     */
    handleLoadedAsset(source) {
        if (!this.loadedAssets.includes(source)) {
            this.loadedAssets.push(source);
        }
        this.updateLoadedStatus();
    }
    /**
     * Update the solid-js signal keeping track of asset loading.
     */
    updateLoadedStatus() {
        setLoaded({
            amountOfAssetsToLoad: this.assetSources.length,
            amountOfAssetsLoaded: this.loadedAssets.length,
        });
    }
    get getPlayerAssets() {
        return this.assets.player;
    }
    get getEnemyAssets() {
        return this.assets.enemy;
    }
    get getWeaponAssets() {
        return this.assets.weapons;
    }
    get getTerrainHeightmap() {
        return this.assets.terrain.heightMap.getAsset;
    }
    get getTerrainNormalmap() {
        return this.assets.terrain.normalMap.getAsset;
    }
    get getSkyboxTexture() {
        return this.assets.terrain.skybox.getAsset;
    }
    static getInstance() {
        if (!AssetLoader.instance)
            AssetLoader.instance = new AssetLoader();
        return AssetLoader.instance;
    }
}
export default AssetLoader;
